import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Check, ChevronLeft, ChevronRight, ExternalLink, GraduationCap } from 'lucide-react';
import {
  Button,
  EmptyState,
  ErrorState,
  Progress,
  Skeleton,
  StatusBadge,
  SubBar,
} from '@/design/components';
import { errorMessage } from '@/lib/errors';
import { nextLesson, progressLabel, useCompleteLesson, useCourse, type Lesson } from './api';
import { LessonBody } from './LessonBody';

/**
 * נגן הלומדה (PRD 5.6.3): שיעור אחד על המסך, סרגל התקדמות, וחזרה
 * לאותו מקום בפעם הבאה.
 *
 * ההתקדמות נשמרת בשרת ולא ב localStorage, כי העובד מתחיל בטלפון
 * וההנהלה סופרת מי השלים. מיקום הקריאה נגזר ממנה: השיעור הראשון שלא
 * סומן. ככה אין מצב שהמסך מראה דבר אחד והדוח דבר אחר.
 */
export function CoursePage() {
  const { courseId = '' } = useParams();
  const course = useCourse(courseId);
  const complete = useCompleteLesson(courseId);
  // מה שנשמר הוא השיעור שהעובד בחר לפתוח, ולא מספר סידורי. בלי בחירה
  // המסך נגזר מההתקדמות עצמה: השיעור הראשון שלא סומן. ככה אין effect
  // שמסנכרן מצב, ואין הבזק של השיעור הלא נכון בטעינה.
  const [chosenId, setChosenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (course.isPending) return <Skeleton className="m-4 h-64" />;
  if (course.isError) return <ErrorState onRetry={() => void course.refetch()} />;

  // ה RPC מחזיר null למי שהלומדה לא הוטלה עליו
  if (!course.data?.course) return <Navigate to="/learning" replace />;

  const meta = course.data.course;
  const lessons = course.data.lessons;
  const done = lessons.filter((lesson) => lesson.completed).length;
  const finished = lessons.length > 0 && done >= lessons.length;

  const chosenIndex = chosenId ? lessons.findIndex((lesson) => lesson.id === chosenId) : -1;
  const resume = nextLesson(lessons);
  // סיימו הכל ולא בחרו כלום: נשארים על השיעור האחרון, לא קופצים לראשון
  const index =
    chosenIndex >= 0
      ? chosenIndex
      : resume
        ? lessons.indexOf(resume)
        : Math.max(0, lessons.length - 1);
  const current = lessons[index] ?? null;
  // מנהל/ת שפותח/ת לומדה שלא הוטלה עליו/ה רואה אותה, ולא מקבל/ת כפתור
  // שהשרת ידחה
  const preview = !course.data.is_assigned;

  const markAndAdvance = (lesson: Lesson) => {
    setError(null);
    complete
      .mutateAsync(lesson.id)
      .then(() => {
        const next = lessons[index + 1];
        if (next) setChosenId(next.id);
      })
      .catch((caught) => setError(errorMessage(caught)));
  };

  return (
    <div className="grid content-start gap-4">
      <SubBar title={meta.title} subtitle={meta.description ?? undefined} />

      <div className="grid gap-4 px-4 pb-8">
        {preview ? (
          <p className="m-0 rounded-md border border-line bg-olive-soft p-3 text-body-sm">
            הלומדה הזו לא הוטלה עליך. אפשר לקרוא אותה, אבל ההתקדמות לא נשמרת.
          </p>
        ) : (
          <Progress value={done} max={lessons.length} label={progressLabel(done, lessons.length)} />
        )}

        {lessons.length === 0 && (
          <EmptyState title="הלומדה עוד ריקה" description="עדיין לא נוספו לה שיעורים." />
        )}

        {current && (
          <article className="grid gap-3 rounded-md border border-line bg-raised p-4">
            <header className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="m-0 font-display text-display-m font-semibold">{current.title}</h2>
              {current.completed ? (
                <StatusBadge tone="good">הושלם</StatusBadge>
              ) : (
                <span className="text-label text-muted">
                  שיעור {index + 1} מתוך {lessons.length}
                </span>
              )}
            </header>

            <LessonBody lesson={current} />

            {error && (
              <p role="alert" className="m-0 text-body-sm text-bad">
                {error}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-2">
              {!current.completed && !preview && (
                <Button
                  variant="primary"
                  disabled={complete.isPending}
                  onClick={() => markAndAdvance(current)}
                >
                  <Check size={20} strokeWidth={1.75} aria-hidden="true" />
                  {index < lessons.length - 1
                    ? 'סיימתי, לשיעור הבא'
                    : 'סיימתי את הלומדה'}
                </Button>
              )}

              {/* RTL: "הבא" הוא שמאלה, "הקודם" ימינה */}
              {index < lessons.length - 1 && (
                <Button variant="secondary" onClick={() => setChosenId(lessons[index + 1]!.id)}>
                  השיעור הבא
                  <ChevronLeft size={20} strokeWidth={1.75} aria-hidden="true" />
                </Button>
              )}
              {index > 0 && (
                <Button variant="secondary" onClick={() => setChosenId(lessons[index - 1]!.id)}>
                  <ChevronRight size={20} strokeWidth={1.75} aria-hidden="true" />
                  השיעור הקודם
                </Button>
              )}
            </div>
          </article>
        )}

        {finished && !preview && meta.quiz_id && !meta.quiz_passed && (
          <div className="grid gap-2 rounded-md border border-line bg-olive-soft p-4">
            <p className="m-0 text-title font-medium">סיימת את כל השיעורים. נשאר החידון.</p>
            <Link
              to={`/learning/quiz/${meta.quiz_id}`}
              className="inline-flex w-fit items-center gap-2 rounded-pill bg-charcoal px-4 py-2 text-body-sm font-medium text-on-charcoal no-underline"
            >
              <GraduationCap size={20} strokeWidth={1.75} aria-hidden="true" />
              לחידון הלומדה
            </Link>
          </div>
        )}

        {lessons.length > 1 && (
          <nav className="grid gap-1">
            <h2 className="m-0 text-body-sm font-medium text-muted">כל השיעורים</h2>
            <ol className="m-0 grid list-none gap-1 p-0">
              {lessons.map((lesson, i) => (
                <li key={lesson.id}>
                  <button
                    type="button"
                    onClick={() => setChosenId(lesson.id)}
                    aria-current={i === index ? 'step' : undefined}
                    className={`flex w-full items-center gap-2 rounded-md border px-3 py-2 text-start text-body-sm ${
                      i === index ? 'border-olive bg-olive-soft' : 'border-line bg-raised'
                    }`}
                  >
                    {lesson.completed ? (
                      <Check
                        size={16}
                        strokeWidth={2}
                        aria-hidden="true"
                        className="shrink-0 text-good"
                      />
                    ) : (
                      <span className="w-4 shrink-0 text-center tabular text-muted">{i + 1}</span>
                    )}
                    <span className="grow">{lesson.title}</span>
                    {lesson.content_type === 'link' && (
                      <ExternalLink size={14} strokeWidth={1.75} aria-hidden="true" />
                    )}
                  </button>
                </li>
              ))}
            </ol>
          </nav>
        )}
      </div>
    </div>
  );
}
